import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import {
  AccessLevel,
  AdminModule,
  AdminModulePermissions,
  AdminRole,
  canAccessModule,
  roleSatisfies,
} from "../roles";
import {
  getAdminModulesFromToken,
  getAdminRoleFromToken,
  getUidFromToken,
} from "../utils/jwt";

export interface AdminRoleInfo {
  /** The signed-in uid (UI hint only — e.g. "assign to me"). */
  uid?: string;
  role: AdminRole | null;
  /** Per-module grants — only ever populated for a manager. */
  modulePermissions: AdminModulePermissions | null;
  /** True when the current role meets or exceeds `minRole` (tiers only). */
  can: (minRole: AdminRole) => boolean;
  /** True when the current principal may reach `module` at `level`. */
  canModule: (
    module: AdminModule,
    level?: Exclude<AccessLevel, "none">,
  ) => boolean;
  isSuperadmin: boolean;
  isManager: boolean;
}

/**
 * Reads the current admin role and module grants from the verified access token
 * (re-read on every token rotation, since it derives from the Redux access
 * token). UI hint only — the backend enforces the real rule.
 */
export const useAdminRole = (): AdminRoleInfo => {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);

  return useMemo(() => {
    const role = getAdminRoleFromToken(accessToken);
    const modulePermissions = getAdminModulesFromToken(accessToken);
    return {
      uid: getUidFromToken(accessToken),
      role,
      modulePermissions,
      can: (minRole: AdminRole) => roleSatisfies(role, minRole),
      canModule: (
        module: AdminModule,
        level: Exclude<AccessLevel, "none"> = "read",
      ) => canAccessModule(role, modulePermissions, module, level),
      isSuperadmin: role === AdminRole.SUPERADMIN,
      isManager: role === AdminRole.MANAGER,
    };
  }, [accessToken]);
};
